/*
	To use this cell, your grid's row data should include an object structured like:

	{
		id,
		onChange,
		isSelected,
	}

	This will render a checkbox that you can use to select the row.

	i.e. When clicked, it will call, where `checked` is the new checked state of the checkbox:
		onChange([id], checked)

	The rendered state of the checkbox is dependent on the `isSelected` flag provided, rather than
	any internal cell component state, so your `onChange` function should handle updating the value
	in the grid row.

	e.g. to use with the `Selected` reducer:

	```
		function SelectableGrid(){
			let columns = [
				{
					key: "idSelector",
					name: "",
					formatter: SelectItem
				},
				...
			];

			let data = [
				{ id: 1 },
				{ id: 2 }
			];

			let selectionNamespace = "some set name";
			let selectedIds = useSelector(state => state.selected[selectionNamespace] || []);

			function selectIds(ids, checked){
				dispatch(selectMultiple(ids, selectionNamespace, checked));
			}

			let selectableData = useMemo(() => {
				return data.map((item) => {
					let isSelected = selectedIds.includes(item.id);
					return {
						...item,
						idSelector: {
							id: item.id,
							isSelected,
							onChange: selectIds,
						},
					};
				});
			}, [data, selectedIds]);

			function getRow(index){
				return selectableData[index];
			}

			return (
				<DataGrid
					columns = {columns}
					rowGetter = {getRow}
				/>
			);
		}
	```

	You could just as easily use this component with local component state by replacing `selectedIds`
	in the example with a state array, and the `selectIds` function with one that updates that array.

	i.e.
	```
		const [selectedIds, setSelectedIds] = useState([]);

		function selectIds(ids, checked){
			let newSelection = new Set(selectedIds);
			if(checked){
				ids.forEach((id) => {
					newSelection.add(id);
				});
			} else {
				ids.forEach((id) => {
					newSelection.delete(id);
				});
			}
			setSelectedIds([...newSelection]);
		}
	```
*/

import React from "react";
import PropTypes from "prop-types";
import { noop } from "utils/common";
import { useShiftClick } from "hooks/useShiftClick";

export default function SelectItem({ value: { id, onChange, isSelected }, dependentValues: { type, shiftSelectConfig = {} } }){
	const shiftClick = useShiftClick(id, type, shiftSelectConfig);

	return (
		<label
			className="checkbox-select-container select-cell"
			onMouseDown={shiftClick}
		>
			<input
				id={id}
				type="checkbox"
				checked = {isSelected}
				className="custom-checkbox"
				onChange = {(e) => {
					if(e.nativeEvent.shiftKey){ return; }
					let { currentTarget: { checked } } = e;

					onChange([id], checked);
				}}
			/>
			<span className="pretty-checkbox" />
		</label>
	);
}

SelectItem.displayName = "SelectItem";

SelectItem.propTypes = {
	value: PropTypes.shape({
		id: PropTypes.number,
		isSelected: PropTypes.bool,
		selector: PropTypes.func,
	}),
};

SelectItem.defaultProps = {
	value: {
		id: -1,
		isSelected: false,
		selector: noop,
	},
};
